import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import ApiService from "../../Core/Service/ApiService";
import { useNavigate, useParams } from "react-router-dom";
import { LondonDate, capitalizeFirstLetter, getImageUrl, validateForm } from "../../Utils/utils";
import { toast } from "react-toastify";
import LoadingIndicator from "../../Components/LoadingIndicator/LoadingIndicator";
import { MDBBtn, MDBCheckbox, MDBCol, MDBInput, MDBRow, MDBSpinner } from "mdb-react-ui-kit";
import { ManagerRole, ManagerRoleOptions, PriceList } from "../../Constants/constants";
import { useSelector } from "react-redux";
import { memoizedGetAuthUser } from "../../Store/Reducer/authReducer";
import { useContext } from "react";
import CmsContext from "../../Context/CmsContext";

const ManagerEdit = React.memo(props => {
    const authUser = useSelector(memoizedGetAuthUser);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const params = useParams();
    const navigate = useNavigate();
    const [permissions, setPermissions] = useState([]);
    const [setPassword, setSetPassword] = useState(params.action != 'edit');
    const [organizations, setOrganizations] = useState([]);
    const [formData, setFormData] = useState({
        role: authUser.role === ManagerRole.SuperAdmin ? ManagerRole.CmsAdmin : ManagerRole.CmsUser,
    });
    let rules = useRef({
        username: {
            required: 'string'
        },
        email: {
            required: 'email'
        },
        role: {
            required: 'string'
        },
        organization_id: {
            required: 'number'
        }
    })
    const [inputErrors, setInputErrors] = useState({});
    const permissionRef = useRef(null);
    const cmsContext = useContext(CmsContext);
    let managerRoleOptions = [];

    if(authUser.role === ManagerRole.SuperAdmin) {
        managerRoleOptions = ManagerRoleOptions.filter(opt=>opt.value != ManagerRole.SuperAdmin);
    } else if(authUser.role === ManagerRole.CmsAdmin) {
        managerRoleOptions = ManagerRoleOptions.filter(opt=>opt.value == ManagerRole.CmsUser);
    }

    useEffect(() => {
        cmsContext.setBreadcrumb([
            { title: cmsContext.currMainMenu?.label ?? ''},
            { title: cmsContext.currSubMenu?.label ?? ''},
            { title: capitalizeFirstLetter(params.action) },
        ])
    }, [])

    useEffect(() => { 
        if(setPassword) {
            rules.current['password'] = {
                required: 'string',
                minlength: 6
            };
            rules.current['check_passwd'] = {
                required: 'string',
                equal: 'password'
            }
        } else {
            delete rules.current.password;
            delete rules.current.check_passwd
        }
    }, [setPassword]);

    const loadData = async () => {
        setLoading(true);

        // To fetch organizations
        let response = await ApiService.get('/organizations/all');
        const organizations = response.data.organizations;
        if(organizations?.length > 0) {
            setOrganizations(organizations.map(org=>({
                value: org.id,
                label: org.name,
            })));
            setFormData({
                ...formData,
                organization_id: organizations[0].id,
            })
        }

        // To fetch manager_permissions
        response = await ApiService.get('/manager_permissions/all');
        const permissions = response.data.rows;

        if(params.action == 'edit' || params.action == 'copy') {
            response = await ApiService.get(`/managers/show/${params.id}`);
            const user = response.data.user;
            delete user.manager_id;
            
            setFormData({
                ...user,
            });

            for(let i=0; i<permissions?.length; i++ ){
                const p = permissions[i];
                p.checked = user.permissions.includes(p.value);
            }
        }

        setPermissions(permissions);

        setLoading(false);
    }

    useEffect(() => {
        loadData();
    }, [params.action]);

    useEffect(() => {
        if(formData.role == ManagerRole.CmsAdmin || formData.role == ManagerRole.SuperAdmin) {
            selectAllPermission();
            permissionRef.current?.classList.add('disabled');
        } else if(formData.role == ManagerRole.CmsUser) {
            permissionRef.current?.classList.remove('disabled');
            
        }
    }, [formData.role]);

    useEffect(() => {
        const ret = validateForm(formData, rules.current);
        setInputErrors(ret.errors);
    }, [formData]);

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        })
    }
    const handleSelectChange = (opt, target) => {
        setFormData({
            ...formData,
            [target]: opt.value
        })
    }
    const selectAllPermission = () => {
        setPermissions(permissions.map(p=>({
            ...p,
            checked: true
        })))
    }
    const unSelectAllPermission = () => {
        setPermissions(permissions.map(p=>({
            ...p,
            checked: false
        })))
    }
    const invertSelectPermission = () => {
        setPermissions(permissions.map(p=>({
            ...p,
            checked: !p.checked
        })))
    }
    const handlePermissionCheckChange = (e, pv) => {
        setPermissions(permissions.map(p=>{
            if(p.value != pv) {
                return p;
            } else {
                return {
                    ...p,
                    checked: e.target.checked
                }
            }
        }));
    }
    const handleActiveChange = (e) => {
        setFormData({
            ...formData,
            deleted: e.target.checked ? 0 : 1
        })
    }
    const handleSave = () => {
        const ret = validateForm(formData, rules.current);
        if(ret.result) {
            setInputErrors({});
            setSaving(true);

            const data = {
                ...formData,
                permissions: permissions.filter(p=>p.checked).map(p=>p.value)
            }
    
            const url = (params.action == 'edit') ? `/managers/save/${params.id}` : '/managers/create';
            ApiService.post(url, data).then(response => {
                navigate(-1);            
            })
            .finally(()=>{
                setSaving(false);
            })
    
        } else {
            setInputErrors(ret.errors)
        }
    }

    return (
    <div className="manager-edit mx-auto" style={{width: 'fit-content'}}>
        <div className="d-flex">
            <div className="mt-4 me-4">
                <label className="required">Username:</label>
                <div style={{width: 300}}>
                    <MDBInput name='username' value={formData.username ?? ''} onChange={handleInputChange} readOnly={params.action=='edit'} />
                </div>
                {inputErrors?.username && <div className="error">{inputErrors.username}</div> }
            </div>
            <div className="mt-4 me-4">
                <label className="required">Email:</label>
                <div style={{width: 300}}>
                    <MDBInput name='email' value={formData.email ?? ''} onChange={handleInputChange} />
                </div>
                {inputErrors?.email && <div className="error">{inputErrors.email}</div> }
            </div>
        </div>

        <div className="d-flex">
            <div className="mt-4 me-4">
                <label className="required">Organization:</label>
                <div style={{width: 300}}>
                    <Select 
                        options={organizations} 
                        value={organizations.find(org=>org.value==formData.organization_id)} 
                        onChange={opt=>handleSelectChange(opt, 'organization_id')}
                    />
                </div>
                {inputErrors?.organization_id && <div className="error">{inputErrors.organization_id}</div>}
            </div>
            <div className="mt-4 me-4">
                <label className="required">Role:</label>
                <div style={{width: 300}}>
                    <Select 
                        options={managerRoleOptions} 
                        value={managerRoleOptions.find(mr=>mr.value==formData.role)} 
                        onChange={opt=>handleSelectChange(opt, 'role')} 
                    />
                </div>
                {inputErrors?.role && <div className="error">{inputErrors.role}</div> }
            </div>
        </div>

        {params.action == 'edit' && (
        <div className="mt-4 d-flex align-items-center">
            <MDBCheckbox name='setPassword' value={setPassword} onChange={e=>setSetPassword(e.target.checked)} />
            <span className="ms-1">Update Password</span>
        </div>
        )}

        {setPassword && (
        <div className={`d-flex ${params.action == 'edit' ? 'mt-2' : 'mt-4'}`}>
            <div className="me-4">
                <label className={`${params.action == 'edit' ? '' : 'required' }`}>Password:</label>
                <div style={{width: 300}}>
                    <MDBInput type='password' name="password" value={formData.password ?? ''} onChange={handleInputChange} />
                </div>
                {inputErrors?.password && <div className="error">{inputErrors.password}</div> }
            </div>
            <div>
                <label className={`${params.action == 'edit' ? '' : 'required' }`}>Check Password:</label>
                <div style={{width: 300}}>
                    <MDBInput type='password' name="check_passwd" value={formData.check_passwd ?? ''} onChange={handleInputChange} />
                </div>
                {inputErrors?.check_passwd && <div className="error">{inputErrors.check_passwd}</div> }
            </div>
        </div>
        )}

        <div className="mt-4" ref={permissionRef}>
            <div className="d-flex align-items-center">
                <label>Permission:</label>
                <MDBBtn className="ms-4 py-1 px-2" color="link" onClick={selectAllPermission}>Select All</MDBBtn>
                <MDBBtn className="ms-2 py-1 px-2" color="link" onClick={unSelectAllPermission}>Select None</MDBBtn>
                <MDBBtn className="ms-2 py-1 px-2" color="link" onClick={invertSelectPermission}>Invert Selection</MDBBtn>
            </div>
            <MDBRow>
                {permissions?.map((p, index)=>(
                    <MDBCol key={index} size={12} md={3} lg={2} className="mt-2 d-flex align-items-center">
                        <MDBCheckbox checked={p.checked ?? false} onChange={e=>handlePermissionCheckChange(e, p.value)} />
                        <span className="ms-1">{p.label}</span>
                    </MDBCol>
                ))}
            </MDBRow>
        </div>

        <div className="mt-4">
            <div className="d-flex align-items-center">
                <MDBCheckbox checked={formData.deleted != 1} onChange={handleActiveChange} />
                <label className="ms-2">Active:</label>
            </div>
        </div>

        <div className="d-flex justify-content-end mt-4">
            <MDBBtn color="default" onClick={e=>navigate(-1)}>Back</MDBBtn>
            <MDBBtn className="united-cms-primary-btn ms-4" onClick={handleSave} disabled={saving}>
                {saving && <MDBSpinner size="sm" className="me-1" />}
                Save
            </MDBBtn>
        </div>

        <LoadingIndicator show={loading} />
    </div>
    )
});

export default ManagerEdit;

